// src/pages/PublicMuultiPage.js
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { usePublicPageMetadata } from '../../hooks/usePublicPageMetadata';
import { NotFoundPage, PageUnavailable } from './PageStatus';
import '../MuultiStyle.css';
import TextComponent from '../TextComponent';
import LinkComponent from '../LinkComponent';
import SocialLinksComponent from '../SocialLinksComponent';
import VideoComponent from "../VideoComponent";
import PublicHeader from "../PublicHeader";
import AudioComponent from "../AudioComponent";
import ImageComponent from "../ImageComponent";
import DonationComponent from "../DonationComponent";
import MintNFTComponent from "../MintNFTComponent";
import HtmlComponent from '../HtmlComponent';
import AdComponent from '../AdComponent';
import ComponentTypeListLinks from '../ComponentTypeListLinks';
import ComponentAnalyticsTracker from '../ComponentAnalyticsTracker';
import { normalizeComponentListSettings } from '../../utils/ComponentTypeLists';
import { choosePreferredUserSnapshot } from '../../../functions/publicMetadataPolicy.js';


const toPositiveInt = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return fallback;
};

const toOptionalPositiveInt = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return null;
};

const normalizeComponentLayout = (raw) => {
    const next = raw || {};
    return {
        colStart: toOptionalPositiveInt(next.colStart),
        rowStart: toOptionalPositiveInt(next.rowStart),
        colSpan: toPositiveInt(next.colSpan, 1),
        rowSpan: toPositiveInt(next.rowSpan, 1),
    };
};

const clampComponentLayout = (layout, renderedColumns, allowHorizontalScroll = false) => {
    const normalized = normalizeComponentLayout(layout);
    const cols = Math.max(1, toPositiveInt(renderedColumns, 1));
    const colSpan = allowHorizontalScroll
        ? normalized.colSpan
        : Math.min(normalized.colSpan, cols);
    let colStart = normalized.colStart;
    if (colStart != null) {
        if (allowHorizontalScroll) {
            colStart = Math.max(1, colStart);
        } else {
            colStart = Math.min(Math.max(1, colStart), cols - colSpan + 1);
        }
    }
    return {
        ...normalized,
        colSpan,
        colStart,
    };
};

const layoutsOverlap = (a, b) => {
    const aColEnd = a.colStart + a.colSpan - 1;
    const bColEnd = b.colStart + b.colSpan - 1;
    const aRowEnd = a.rowStart + a.rowSpan - 1;
    const bRowEnd = b.rowStart + b.rowSpan - 1;
    return !(aColEnd < b.colStart || bColEnd < a.colStart || aRowEnd < b.rowStart || bRowEnd < a.rowStart);
};

const findFirstOpenSlot = (placed, desired, renderedColumns, allowHorizontalScroll = false) => {
    const minMaxColStart = Math.max(1, renderedColumns - desired.colSpan + 1);
    const maxColStart = allowHorizontalScroll
        ? Math.max(minMaxColStart, desired.colStart || 1, 256)
        : minMaxColStart;
    let col = Math.min(Math.max(1, desired.colStart || 1), maxColStart);
    let row = Math.max(1, desired.rowStart || 1);
    const maxIterations = 15000;

    for (let i = 0; i < maxIterations; i += 1) {
        const candidate = {
            colStart: col,
            rowStart: row,
            colSpan: desired.colSpan,
            rowSpan: desired.rowSpan,
        };
        const hasCollision = placed.some((p) => layoutsOverlap(candidate, p));
        if (!hasCollision) return candidate;
        col += 1;
        if (col > maxColStart) {
            col = 1;
            row += 1;
        }
    }

    return {
        colStart: 1,
        rowStart: Math.max(1, row),
        colSpan: desired.colSpan,
        rowSpan: desired.rowSpan,
    };
};

const resolveCollisionSafeLayouts = (
    components,
    inputLayouts,
    renderedColumns,
    allowHorizontalScroll = false
) => {
    const cols = Math.max(1, toPositiveInt(renderedColumns, 1));
    const componentIds = (components || []).map((comp, idx) => String(comp?.id ?? idx));
    const placed = [];
    const nextLayouts = {};

    componentIds.forEach((id) => {
        const normalized = clampComponentLayout(
            normalizeComponentLayout(inputLayouts?.[id]),
            cols,
            allowHorizontalScroll
        );
        const desired = {
            colStart: normalized.colStart ?? 1,
            rowStart: normalized.rowStart ?? 1,
            colSpan: normalized.colSpan,
            rowSpan: normalized.rowSpan,
        };
        const placedSlot = findFirstOpenSlot(placed, desired, cols, allowHorizontalScroll);
        const resolved = {
            ...normalized,
            colStart: placedSlot.colStart,
            rowStart: placedSlot.rowStart,
        };
        nextLayouts[id] = resolved;
        placed.push(resolved);
    });

    return nextLayouts;
};

const getLayoutViewportFromWidth = (width) => {
    const safeWidth = Math.max(0, Number.parseInt(width, 10) || 0);
    if (safeWidth > 0 && safeWidth <= 600) return 'mobile';
    if (safeWidth > 0 && safeWidth <= 1024) return 'tablet';
    return 'desktop';
};

const extractUrlFromCssBackgroundImage = (bgImage) => {
    if (!bgImage || typeof bgImage !== 'string') return '';
    const match = bgImage.match(/url\((['"]?)(.*?)\1\)/i);
    return match?.[2] || '';
};

const normalizeImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^url\(/i.test(trimmed)) return extractUrlFromCssBackgroundImage(trimmed);
    return trimmed;
};

export default function PublicMuultiPage() {
    const { username } = useParams();
    const [muulti, setMuulti] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [columnsCount, setColumnsCount] = useState('auto-fill');
    const [minWidthPx,   setMinWidthPx]   = useState(400);
    // const [maxWidthPx,   setMaxWidthPx]   = useState(1000);
    const [gridGap, setGridGap] = useState(24);
    const [advancedGridLayout, setAdvancedGridLayout] = useState(false);
    const [allowHorizontalScroll, setAllowHorizontalScroll] = useState(false);
    const [lockContainerWidth, setLockContainerWidth] = useState(true);
    const [gridRowHeightPx, setGridRowHeightPx] = useState(120);
    const [canvasMaxWidthPx, setCanvasMaxWidthPx] = useState(0);
    const [componentLayouts, setComponentLayouts] = useState({});
    const [componentLayoutsTablet, setComponentLayoutsTablet] = useState({});
    const [componentLayoutsMobile, setComponentLayoutsMobile] = useState({});
    const [gridViewportWidth, setGridViewportWidth] = useState(0);
    const componentsGridRef = useRef(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const usersRef = collection(db, 'users');
                let data = null;

                const q = query(usersRef, where('muultiName', '==', username));
                const snap = await getDocs(q);
                const preferredDoc = choosePreferredUserSnapshot(snap.docs);
                data = preferredDoc ? (preferredDoc.data() || {}) : null;

                if (data) {
                    setMuulti(data);

                                            // hydrate grid settings
                                    if (data.gridGap != null) setGridGap(data.gridGap);
                                    if (data.columnsCount) setColumnsCount(data.columnsCount);
                                    if (data.minWidthPx)   setMinWidthPx(data.minWidthPx);
                                    let shouldEnableAdvancedFromData = data.advancedGridLayout != null
                                        ? !!data.advancedGridLayout
                                        : false;
                                    if (data.allowHorizontalScroll != null) setAllowHorizontalScroll(!!data.allowHorizontalScroll);
                                    if (data.lockContainerWidth != null) setLockContainerWidth(!!data.lockContainerWidth);
                                    if (data.gridRowHeightPx != null) setGridRowHeightPx(toPositiveInt(data.gridRowHeightPx, 120));
                                    if (data.canvasMaxWidthPx != null) setCanvasMaxWidthPx(toPositiveInt(data.canvasMaxWidthPx, 0));
                                    if (data.componentLayouts && typeof data.componentLayouts === 'object') {
                                        setComponentLayouts(data.componentLayouts);
                                        if (Object.keys(data.componentLayouts).length > 0) {
                                            shouldEnableAdvancedFromData = true;
                                        }
                                    }
                                    if (data.componentLayoutsTablet && typeof data.componentLayoutsTablet === 'object') {
                                        setComponentLayoutsTablet(data.componentLayoutsTablet);
                                        if (Object.keys(data.componentLayoutsTablet).length > 0) {
                                            shouldEnableAdvancedFromData = true;
                                        }
                                    } else {
                                        setComponentLayoutsTablet({});
                                    }
                                    if (data.componentLayoutsMobile && typeof data.componentLayoutsMobile === 'object') {
                                        setComponentLayoutsMobile(data.componentLayoutsMobile);
                                        if (Object.keys(data.componentLayoutsMobile).length > 0) {
                                            shouldEnableAdvancedFromData = true;
                                        }
                                    } else {
                                        setComponentLayoutsMobile({});
                                    }
                                    setAdvancedGridLayout(shouldEnableAdvancedFromData);
                                    // if (data.maxWidthPx)   setMinWidthPx(data.maxWidthPx);
                } else {
                    setMuulti(undefined);
                }
            } catch (e) {
                console.error(e);
                setLoadError(true);
                setMuulti(undefined);
            }
            setLoading(false);
        })();
    }, [username]);

    usePublicPageMetadata(muulti, loading || loadError);

    useLayoutEffect(() => {
        if (loading) return;
        const updateGridWidth = () => {
            const gridEl = componentsGridRef.current;
            const nextWidth = gridEl
                ? Math.max(0, Math.round(gridEl.getBoundingClientRect()?.width || 0))
                : 0;
            setGridViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
        };

        updateGridWidth();
        const gridEl = componentsGridRef.current;
        let observer = null;
        if (gridEl && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => updateGridWidth());
            observer.observe(gridEl);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateGridWidth);
        }
        return () => {
            if (observer) observer.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateGridWidth);
            }
        };
    }, [loading]);




    if (loading) return <div className="muulti-container"><p>Loading…</p></div>;
    if (loadError) return <PageUnavailable />;
    if (!muulti) return <NotFoundPage />;

     // pull template & background from the loaded data (with a default fallback)
    const template = muulti.template || '';
      const bg = muulti.background || { type: 'single', color: '#ffffff' };
      const styleProps = (() => {
            const imageUrl =
                normalizeImageUrl(bg.imageUrl) ||
                normalizeImageUrl(bg.url) ||
                normalizeImageUrl(bg.image) ||
                normalizeImageUrl(bg.src) ||
                extractUrlFromCssBackgroundImage(bg.backgroundImage) ||
                extractUrlFromCssBackgroundImage(bg.background) ||
                '';
            const normalizedType = (() => {
                const rawType = typeof bg.type === 'string' ? bg.type.toLowerCase() : bg.type;
                if (rawType === 'cover') return 'image';
                if (rawType) return rawType;
                if (imageUrl) return 'image';
                if (Array.isArray(bg.colors) && bg.colors.length >= 2) return 'gradient';
                if (bg.color || bg.backgroundColor) return 'single';
                return 'none';
            })();
            if (normalizedType === 'single') {
                  return { backgroundColor: bg.color || bg.backgroundColor || '#ffffff' };
                }
            if (normalizedType === 'gradient') {
                  const colors = Array.isArray(bg.colors) && bg.colors.length >= 2
                    ? bg.colors
                        : ['#ffffff', '#000000'];
                  const direction = bg.direction || 'to right';
                  return { backgroundImage: `linear-gradient(${direction}, ${colors[0]}, ${colors[1]})` };
                }
            if (normalizedType === 'image') {
                  return {
                        backgroundColor:   bg.backgroundColor || bg.color || '#ffffff',
                        backgroundImage:   imageUrl ? `url("${imageUrl}")` : undefined,
                        backgroundSize:    bg.sizing || bg.backgroundSize || 'cover',          // use value directly
                        backgroundPosition:bg.position || bg.backgroundPosition || 'center',
                        backgroundRepeat:  bg.repeatMode || bg.backgroundRepeat || 'no-repeat',
                      };
                }
            return {};
          })();



    const hb = muulti.headerBackground || {
        type: 'color',
        color: '#ffffff',
        colors: ['#ffffff', '#000000'],
        fontColor: '#000000',
        direction: 'to right',
        imageUrl: '',
        sizing: 'cover',
        position: 'center'
    };

    const headerStyleProps = (() => {
        let base;
        const headerImageUrl =
            normalizeImageUrl(hb.imageUrl) ||
            normalizeImageUrl(hb.url) ||
            normalizeImageUrl(hb.image) ||
            normalizeImageUrl(hb.src) ||
            extractUrlFromCssBackgroundImage(hb.backgroundImage) ||
            extractUrlFromCssBackgroundImage(hb.background) ||
            '';
        const headerRawType = typeof hb.type === 'string' ? hb.type.toLowerCase() : hb.type;
        const headerType = headerRawType === 'cover' ? 'image' : headerRawType;
        switch (headerType) {
            case 'color':
                base = { backgroundColor: hb.color };
                break;
            case 'gradient':
                base = {
                    backgroundImage: `linear-gradient(${hb.direction}, ${hb.colors[0]}, ${hb.colors[1]})`
                };
                break;
            case 'image':
                base = {
                    backgroundColor: hb.fallback || hb.backgroundColor || hb.color || '#ffffff',
                    backgroundImage: headerImageUrl ? `url("${headerImageUrl}")` : undefined,
                    backgroundSize: hb.sizing || hb.backgroundSize || 'cover',
                    backgroundPosition: hb.position || hb.backgroundPosition || 'center',
                    backgroundRepeat: hb.repeatMode || hb.backgroundRepeat || 'no-repeat'
                };
                break;
            default:
                base = {};
        }
            return {
                  ...base,
                  color:        hb.fontColor,
                  borderStyle:  hb.borderStyle || 'none',
                  borderWidth:  hb.borderWidth ?? 1,         // number => px
                    borderColor:  hb.borderColor || 'transparent',
                    // padding:      hb.padding ?? 24,            // number => px, or string like "10%"
                  boxSizing:    'border-box',
                  backgroundOrigin: 'border-box',
                  backgroundClip:   'border-box'
            };
    })();

    const detectedLayoutViewport = (() => {
        const fallbackWidth = typeof window !== 'undefined'
            ? Math.max(0, Number.parseInt(window.innerWidth, 10) || 0)
            : 0;
        return getLayoutViewportFromWidth(gridViewportWidth || fallbackWidth);
    })();
    const viewportOverrideLayouts = (() => {
        if (detectedLayoutViewport === 'tablet') return componentLayoutsTablet || {};
        if (detectedLayoutViewport === 'mobile') return componentLayoutsMobile || {};
        return {};
    })();
    const activeComponentLayouts = (() => {
        if (detectedLayoutViewport === 'desktop') return componentLayouts || {};
        return {
            ...(componentLayouts || {}),
            ...(viewportOverrideLayouts || {}),
        };
    })();
    const shouldUseAdvancedLayout =
        !!advancedGridLayout ||
        Object.keys(componentLayouts || {}).length > 0 ||
        Object.keys(componentLayoutsTablet || {}).length > 0 ||
        Object.keys(componentLayoutsMobile || {}).length > 0;
    const explicitColumnsCount = (() => {
        const parsed = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        return null;
    })();
    const safeMinColumnWidthPx = Math.max(80, toPositiveInt(minWidthPx, 400));
    const componentGridTemplateColumns = (() => {
        if (detectedLayoutViewport === 'mobile' && !allowHorizontalScroll) {
            return '1fr';
        }
        if (explicitColumnsCount != null) {
            if (shouldUseAdvancedLayout && !allowHorizontalScroll) {
                return `repeat(${explicitColumnsCount}, minmax(0, 1fr))`;
            }
            return `repeat(${explicitColumnsCount}, minmax(${safeMinColumnWidthPx}px, 1fr))`;
        }
        return `repeat(auto-fill, minmax(${safeMinColumnWidthPx}px, 1fr))`;
    })();

    const getRenderedColumnCount = () => {
        if (detectedLayoutViewport === 'mobile' && !allowHorizontalScroll) {
            return 1;
        }
        const parsed = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        const gridEl = componentsGridRef.current;
        const observedWidth = Math.max(0, gridViewportWidth || 0);
        if (gridEl) {
            const rect = gridEl.getBoundingClientRect();
            const width = observedWidth || rect?.width || 0;
            const gap = Math.max(0, Number.parseInt(gridGap, 10) || 0);
            const minCol = Math.max(80, toPositiveInt(minWidthPx, 400));
            if (width > 0) {
                return Math.max(1, Math.floor((width + gap) / (minCol + gap)));
            }
        } else if (observedWidth > 0) {
            const gap = Math.max(0, Number.parseInt(gridGap, 10) || 0);
            const minCol = Math.max(80, toPositiveInt(minWidthPx, 400));
            return Math.max(1, Math.floor((observedWidth + gap) / (minCol + gap)));
        }
        return 1;
    };

    const collisionSafeLayouts = shouldUseAdvancedLayout
        ? resolveCollisionSafeLayouts(
            muulti?.components || [],
            activeComponentLayouts || {},
            getRenderedColumnCount(),
            allowHorizontalScroll
        )
        : {};

    const getComponentWrapperStyle = (compId) => {
        if (!shouldUseAdvancedLayout || compId == null) return {};
        const layout = collisionSafeLayouts?.[String(compId)] || clampComponentLayout(
            normalizeComponentLayout(activeComponentLayouts?.[String(compId)]),
            getRenderedColumnCount(),
            allowHorizontalScroll
        );
        const rowHeight = Math.max(40, toPositiveInt(gridRowHeightPx, 120));
        return {
            gridColumn: layout.colStart != null
                ? `${layout.colStart} / span ${layout.colSpan}`
                : `span ${layout.colSpan}`,
            gridRow: layout.rowStart != null
                ? `${layout.rowStart} / span ${layout.rowSpan}`
                : `span ${layout.rowSpan}`,
            minHeight: `${layout.rowSpan * rowHeight}px`,
            position: 'relative',
        };
    };
    const componentListSettings = normalizeComponentListSettings(muulti.componentListSettings);
    const componentTypeListLinks = (
        <ComponentTypeListLinks
            components={muulti.components || []}
            componentListSettings={componentListSettings}
            publicKey={muulti.publicKey || ''}
            username={muulti.muultiName || username || ''}
            showFollowButton
            showFollowerCount={muulti.showFollowerCount === true}
        />
    );
    const componentTypeListLinksInHeader = componentListSettings.placement === 'inHeader';


    return (
        <div
            className={`muulti-container ${template}`}
            style={{
                ...styleProps,
                width: shouldUseAdvancedLayout
                    ? (lockContainerWidth ? '100%' : undefined)
                    : '100%',
                maxWidth: shouldUseAdvancedLayout && lockContainerWidth ? '100%' : undefined,
                overflowX: shouldUseAdvancedLayout
                    ? (allowHorizontalScroll ? 'auto' : 'hidden')
                    : undefined,
            borderStyle:   muulti.containerFrame?.borderStyle,
                borderWidth:   muulti.containerFrame?.borderWidth ?? undefined,   // number => px
                borderColor:   muulti.containerFrame?.borderColor,
                borderRadius:  muulti.containerFrame?.borderRadius ?? undefined,  // number => px
                paddingTop:    muulti.containerFrame?.paddingTop ?? undefined,    // number or "%", works both ways
                paddingRight:  muulti.containerFrame?.paddingRight ?? undefined,
                paddingBottom: muulti.containerFrame?.paddingBottom ?? undefined,
                paddingLeft:   muulti.containerFrame?.paddingLeft ?? undefined,
                boxSizing: 'border-box',
            }}
        >

                   <PublicHeader
                       profilePicUrl={muulti.profilePicUrl}
                       pageTitle={muulti.pageTitle || muulti.muultiName}
                       pageDescription={muulti.pageDescription}
                       alignment={muulti.alignment}
                       headerPadding={muulti.headerPadding}
                         style={headerStyleProps}
               >
                       {componentTypeListLinksInHeader ? componentTypeListLinks : null}
               </PublicHeader>

                   {!componentTypeListLinksInHeader ? componentTypeListLinks : null}

                   <div
                       className={`public-components${shouldUseAdvancedLayout ? ' layout-managed-grid' : ''}`}
                       ref={componentsGridRef}
                       style={{
                           gridTemplateColumns: componentGridTemplateColumns,
                           gridGap: `${gridGap}px`,
                           gridAutoRows: shouldUseAdvancedLayout ? `minmax(${Math.max(40, toPositiveInt(gridRowHeightPx, 120))}px, auto)` : undefined,
                           gridAutoFlow: shouldUseAdvancedLayout ? 'row' : undefined,
                           maxWidth: shouldUseAdvancedLayout && canvasMaxWidthPx > 0 ? `${canvasMaxWidthPx}px` : undefined,
                           marginInline: shouldUseAdvancedLayout ? 'auto' : undefined,
                           width: '100%',
                           minWidth: 0,
                           overflowX: shouldUseAdvancedLayout
                               ? (allowHorizontalScroll ? 'auto' : 'hidden')
                               : undefined,
                       }}
                   >
                                  {muulti.components?.map((comp, idx) => {
                                      // build the props you need
                                      const commonProps = {
                                          comp,
                                          style: comp.style || {},
                                          pageTypography: muulti.typography || null,
                                          isEditor: false, // 👈 PUBLIC PAGE: never bypass gates
                                          ownerPublicKey: muulti.publicKey || '',
                                      };

                                      let componentNode = null;
                                      switch (comp.type) {
                                          case 'text':
                                              componentNode = <TextComponent {...commonProps} />;
                                              break;

                                          case 'mint':
                                              componentNode = (
                                                  <MintNFTComponent
                                                      {...commonProps}
                                                      comp={comp}
                                                      style={comp.style || {}}
                                                  />
                                              );
                                              break;

                                          case 'donation':
                                              {
                                                      // ensure we always have a recipientPublicKey
                                                      const donationComp = {
                                                        ...comp,
                                                        recipientPublicKey: comp.recipientPublicKey || muulti.publicKey || ''
                                                  };
                                                      componentNode = (
                                                        <DonationComponent
                                                        {...commonProps}
                                                        comp={donationComp}
                                                        style={donationComp.style || {}}
                                                        pageTypography={muulti.typography || null}
                                                        />
                                                      );

                                                      break;
                                              }

                                          case 'link':
                                              componentNode = <LinkComponent {...commonProps} />;
                                              break;
                                          case 'social':
                                              componentNode = <SocialLinksComponent {...commonProps} />;
                                              break;
                                          case 'image':
                                              componentNode = <ImageComponent {...commonProps} />;
                                              break;
                                          case 'video':
                                              componentNode = <VideoComponent {...commonProps} />;
                                              break;
                                          case 'html':
                                              componentNode = <HtmlComponent {...commonProps} />;
                                              break;

                                          case 'audio':
                                              componentNode = <AudioComponent {...commonProps} />;
                                              break;
                                          case 'ad':
                                              componentNode = <AdComponent {...commonProps} />;
                                              break;
                                          default:
                                              componentNode = null;
                                              break;
                                      }
                                      if (!componentNode) return null;
                                      const panelId = String(comp?.id ?? idx);
                                      return (
                                          <div
                                              key={panelId}
                                              id={`public-panel-${panelId}`}
                                              className="component-grid-item"
                                              style={getComponentWrapperStyle(panelId)}
                                          >
                                              <ComponentAnalyticsTracker
                                                  comp={comp}
                                                  muulti={muulti}
                                                  surface="public"
                                              >
                                                  {componentNode}
                                              </ComponentAnalyticsTracker>
                                          </div>
                                      );
                                  })}


            </div>
        </div>
    );
}
